import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from 'sonner';
import type { PermissionType } from '@/hooks/useRoleManagement';
import type { Workspace } from '@/types/workspace';
import { useModuleAccess } from '@/hooks/useModuleAccess';

interface WorkspaceContextType {
  currentWorkspace: Workspace | null;
  isLoading: boolean;
  error: Error | null;
  contextStatus: 'loading' | 'ready' | 'error';
  isOwner: boolean;
  hasPermission: (resource: string, permissionType?: PermissionType) => Promise<boolean>;
  hasPermissionSync: (resource: string) => boolean;
  hasModule: (moduleId: string) => boolean;
  switchWorkspace: (workspaceId: string) => void;
  brand: any;
  clearWorkspace: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export { WorkspaceContext };

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { permissions: userPermissions } = usePermissions();
  const { workspaceId: paramWorkspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Resolve the active workspaceId from either route param or session storage
  const resolveWorkspaceId = (): string | null => {
    if (paramWorkspaceId) return paramWorkspaceId;
    try {
      const id = sessionStorage.getItem('currentWorkspaceId');
      if (id) return id;
    } catch (_) {}
    try {
      const wsRaw = sessionStorage.getItem('workspaceData');
      if (wsRaw) {
        const parsed = JSON.parse(wsRaw || '{}');
        if (parsed?.workspaceId) return parsed.workspaceId;
      }
    } catch (_) {}
    return null;
  };
  const workspaceId = resolveWorkspaceId() || undefined;


  const queryEnabled = Boolean(user?.id && workspaceId);

  // If no workspace could be resolved, redirect to the selector so the user can pick one.
  useEffect(() => {
    if (!workspaceId) {
      // Avoid loops if we're already on the selection page
      if (!location.pathname.startsWith('/select-workspace')) {
        navigate('/select-workspace', { replace: true });
      }
    }
  }, [workspaceId, navigate, location.pathname]);

  // CRITICAL: Only the main workspace query affects contextStatus
  // Construct the current workspace from session storage only (no API calls)
  const workspace: Workspace | null = useMemo(() => {
    if (!queryEnabled) return null;
    try {
      const wsRaw = sessionStorage.getItem('workspaceData');
      const parsed = wsRaw ? JSON.parse(wsRaw) : {};
      const id = parsed?.workspaceId || workspaceId;
      const name = parsed?.workspaceName || 'Workspace';
      const ownerUserId = parsed?.owner_user_id || parsed?.ownerId || parsed?.userId || '';
      const now = new Date().toISOString();
      const stub: Workspace = {
        id,
        name,
        description: parsed?.description || undefined,
        owner_user_id: ownerUserId || '',
        is_default: Boolean(parsed?.is_default),
        created_at: parsed?.created_at || now,
        updated_at: parsed?.updated_at || now,
        role: parsed?.role || undefined,
        organization: parsed?.organization || undefined,
      };
      return stub;
    } catch (_) {
      return null;
    }
  }, [queryEnabled, workspaceId]);

  const isLoading = useMemo(() => !queryEnabled || !workspace, [queryEnabled, workspace]);
  const error = useMemo(() => (queryEnabled && !workspace ? (new Error('Workspace not available')) as Error : null), [queryEnabled, workspace]);

  // Remove the extra list call for owner detection; derive from entitlements or the workspace payload itself
  const isOwnerFromWorkspace = useMemo(() => {
    if (!user?.id || !workspace) return false;
    const ownerId = (workspace as any).owner_user_id || (workspace as any).owner_id || (workspace as any).ownerId;
    return ownerId ? ownerId === user.id : false;
  }, [user?.id, workspace]);

  // Use unified module access hook
  const moduleAccess = useModuleAccess('workspace', workspaceId || '');

  const contextStatus = useMemo((): 'loading' | 'ready' | 'error' => {

    // Context status depends ONLY on the workspace query
    if (!queryEnabled) return 'loading';
    if (isLoading) return 'loading';
    if (error) return 'error';
    if (!workspace) return 'loading';
    return 'ready';
  }, [queryEnabled, isLoading, error, workspace]);

  // Analytics queries - wrapped to NOT affect contextStatus
  useQuery({
    queryKey: ['workspace-analytics', workspaceId],
    queryFn: async () => {
      if (!workspace) return null;
      
      try {
        // Placeholder for analytics - will be implemented later
        return null;
      } catch (error) {
        console.warn('[Analytics] Non-critical error (ignored):', error);
        return null;
      }
    },
    enabled: contextStatus === 'ready',
    retry: 0,
  });

  // Owner detection with fallback - prioritize moduleAccess result
  const isOwner = useMemo(() => {
    // Primary: entitlements-based owner detection
    if (moduleAccess.isOwner) return true;
    
    // Secondary: workspace payload owner match (no extra API calls)
    if (isOwnerFromWorkspace) return true;
    
    // Fallback: email-based check
    if (user?.email === 'hari@billportal.io') return true;
    
    // Fallback: workspace owner_id check
    if (workspace?.owner_id === user?.id) return true;
    
    // Fallback: permissions system
    if (userPermissions?.isOwner) return true;
    
    return false;
  }, [moduleAccess.isOwner, isOwnerFromWorkspace, user?.email, user?.id, userPermissions?.isOwner, workspace]);

  const hasPermission = async (resource: string, permissionType: PermissionType = 'read'): Promise<boolean> => {
    // Owner/Super Admin bypass
    if (isOwner || userPermissions?.hasFullAccess) return true;
    
    return userPermissions?.checkPermission(resource) || false;
  };

  const hasPermissionSync = (resource: string): boolean => {
    // Owner/Super Admin bypass  
    if (isOwner || userPermissions?.hasFullAccess) return true;
    
    // For non-admin users, show basic access for dashboard/bills
    if (resource === 'dashboard' || resource === 'bills') return true;
    
    return false;
  };

  // Server-side module check with owner bypass using unified entitlements
  const hasModule = (moduleId: string): boolean => {
    
    // Use the unified module access hook
    return moduleAccess.hasModule(moduleId);
  };

  const switchWorkspace = (newWorkspaceId: string) => {
    const currentPath = location.pathname;
    const pathWithoutWorkspace = currentPath.replace(/\/w\/[^\/]+/, '');
    navigate(`/w/${newWorkspaceId}${pathWithoutWorkspace || '/dashboard'}`);
    
    // Force refresh to clear state
    setTimeout(() => {
      queryClient.invalidateQueries();
    }, 100);
  };

  const clearWorkspace = () => {
    queryClient.removeQueries({ queryKey: ['workspace'] });
    queryClient.removeQueries({ queryKey: ['is-owner'] });
    queryClient.removeQueries({ queryKey: ['workspaces'] });
    try { sessionStorage.removeItem('currentWorkspaceId'); } catch (e) {}
    try { sessionStorage.removeItem('workspaceData'); } catch (e) {}
    try { require('@/services/authStore').setAuthStore({ workspaceData: null }); } catch (e) {}
  };

  // Clear workspace data only on explicit sign-out conditions.
  // Avoid clearing on transient user null/undefined to prevent redirect loops.
  useEffect(() => {
    try {
      // If user is present, do nothing
      if (user) return;

      // Check persisted auth signals before clearing
      const store = (require('@/services/authStore').getAuthStore?.() || {}) as any;
      const token = store?.authToken;
      const persistedUser = typeof window !== 'undefined' ? sessionStorage.getItem('userData') : null;

      // Only clear if we truly have no auth context
      if (!token && !persistedUser) {
        clearWorkspace();
      }
    } catch (_) {
      // Fail-safe: do not clear on error
    }
  }, [user]);

  const value: WorkspaceContextType = {
    currentWorkspace: workspace || null,
    isLoading,
    error: error as Error | null,
    contextStatus,
    isOwner,
    hasPermission,
    hasPermissionSync,
    hasModule,
    switchWorkspace,
    brand: {},
    clearWorkspace,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = (): WorkspaceContextType | null => {
  const context = useContext(WorkspaceContext);
  return context || null;
};